import type { ActiveScopes } from '@post-office/shared-contracts/util';

export const EXCLUDED_SCOPE_NAMES = ['welcome/software', 'jira/get-started'] as const;

export type CoreActiveScopesCheckResult =
	| {
			outcome: 'active';
			activeScopes: ActiveScopes;
			intersectedScopes: string[];
			nonIntersectedScopes: string[];
	  }
	| {
			outcome: 'no-active-scopes';
			activeScopes: undefined;
			intersectedScopes: [];
			nonIntersectedScopes: string[];
	  }
	| {
			outcome: 'excluded-scope';
			activeScopes: ActiveScopes;
			excludedScopes: string[];
			intersectedScopes: [];
			nonIntersectedScopes: string[];
	  }
	| {
			outcome: 'no-required-scopes';
			activeScopes: ActiveScopes;
			intersectedScopes: [];
			nonIntersectedScopes: [];
	  }
	| {
			outcome: 'no-match';
			activeScopes: ActiveScopes;
			intersectedScopes: [];
			nonIntersectedScopes: string[];
	  };

export type CoreActiveScopesCheckParams = {
	activeScopes: ActiveScopes | undefined;
	requiredScopes: Array<{ name: string } | string>;
	request: { logger: { info: (context: Record<string, unknown>, message: string) => void } };
};

export const coreActiveScopesCheck = ({
	activeScopes,
	requiredScopes,
	request,
}: CoreActiveScopesCheckParams): CoreActiveScopesCheckResult => {
	const log = (message: string, context?: Record<string, unknown>) =>
		request.logger.info({ message, ...context }, 'coreActiveScopesCheck');
	const requiredScopeNames = requiredScopes.map((s) => (typeof s === 'string' ? s : s.name));

	if (!activeScopes) {
		log('Active scopes not found in context', { requiredScopes: requiredScopeNames });
		return {
			outcome: 'no-active-scopes',
			activeScopes: undefined,
			intersectedScopes: [],
			nonIntersectedScopes: requiredScopeNames,
		};
	}

	const activeScopeNames = activeScopes.current.map((s) => s.name);

	const excludedScopes = activeScopeNames.filter((name) =>
		EXCLUDED_SCOPE_NAMES.includes(name as (typeof EXCLUDED_SCOPE_NAMES)[number]),
	);
	if (excludedScopes.length > 0) {
		log('Active scope matches excluded route — denying', { excludedScopes });
		return {
			outcome: 'excluded-scope',
			activeScopes,
			excludedScopes,
			intersectedScopes: [],
			nonIntersectedScopes: requiredScopeNames,
		};
	}

	if (requiredScopeNames.length === 0) {
		log('No required scopes provided');
		return {
			outcome: 'no-required-scopes',
			activeScopes,
			intersectedScopes: [],
			nonIntersectedScopes: [],
		};
	}

	const intersectedScopes = requiredScopeNames.filter((name) => activeScopeNames.includes(name));
	const nonIntersectedScopes = requiredScopeNames.filter(
		(name) => !activeScopeNames.includes(name),
	);

	if (intersectedScopes.length > 0) {
		return { outcome: 'active', activeScopes, intersectedScopes, nonIntersectedScopes };
	}

	log('None of the required scopes are active', {
		requiredScopes: requiredScopeNames,
		activeScopeNames,
	});
	return {
		outcome: 'no-match',
		activeScopes,
		intersectedScopes: [],
		nonIntersectedScopes: requiredScopeNames,
	};
};
